import { create } from "zustand";
import {
  type Artist,
  type LpcLayers,
  listArtists,
  createArtist as apiCreate,
  updateArtist as apiUpdate,
  deleteArtist as apiDelete,
} from "../infra/api.js";
import type { ArtistPersona, SonicTraits } from "../domain/index.js";

interface ArtistState {
  artists: Artist[];
  loading: boolean;
  error: string | null;
  fetch: () => Promise<void>;
  create: (input: {
    name: string;
    lpc_layers: LpcLayers;
    sonic_traits?: SonicTraits;
    persona?: ArtistPersona;
  }) => Promise<Artist>;
  update: (
    id: string,
    input: {
      name?: string;
      lpc_layers?: LpcLayers;
      sonic_traits?: SonicTraits;
      persona?: ArtistPersona;
    },
  ) => Promise<Artist>;
  remove: (id: string) => Promise<void>;
}

export const useArtistStore = create<ArtistState>((set, get) => ({
  artists: [],
  loading: false,
  error: null,

  async fetch() {
    set({ loading: true, error: null });
    try {
      const { artists } = await listArtists();
      set({ artists, loading: false });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : String(e), loading: false });
    }
  },

  async create(input) {
    const { artist } = await apiCreate(input);
    set({ artists: [artist, ...get().artists] });
    return artist;
  },

  async update(id, input) {
    const { artist } = await apiUpdate(id, input);
    set({ artists: get().artists.map((a) => (a.id === id ? artist : a)) });
    return artist;
  },

  async remove(id) {
    await apiDelete(id);
    set({ artists: get().artists.filter((a) => a.id !== id) });
  },
}));
